import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import {
  type CustomMarkerAnchorPoint,
  type GoogleMap,
  type GoogleMapLatLngLiteral,
  MapMarkerType,
  type SearchMapMarker,
} from '@global-types/googleMap.ts';
import { currencyNoDecimal } from '@tools/number-formatter';
import { useSearchStore } from '@stores/useSearchStore.ts';
import type { ExtendedAlgoliaOffer } from '@global-types/algoliaOffer';
import { useGoogleMap } from '@composables/useGoogleMap';
import { getTotalMonthlyPrice } from '@pages/search/serializer/deserializedOffer';

export const useSearchMapStore = defineStore('searchMap', () => {
  const {
    offers,
    isLoading,
    perPage,
  } = storeToRefs(useSearchStore());

  const {
    map,
    zoomIn,
    zoomOut,
    initMap: initGoogleMap,
    fitMapToBounds,
    DEFAULT_FRANCE_CENTER,
    getFranceBounds,
  } = useGoogleMap();

  const center = ref<GoogleMapLatLngLiteral>(DEFAULT_FRANCE_CENTER);
  const customMarkerAnchorPoint = ref<CustomMarkerAnchorPoint>('BOTTOM_RIGHT');
  const userLocation = ref<GoogleMapLatLngLiteral | null>(null);

  const getOffsetLatLng = ({
    lat,
    lng,
    index,
    total,
  }: {
    lat: number,
    lng: number,
    index: number,
    total: number,
  }): GoogleMapLatLngLiteral => {
    if (total <= 1) {
      return { lat, lng };
    }

    const offsetRadiusDegrees = 0.00003;
    const angle = (index / total) * 2 * Math.PI;
    const newLat = lat + offsetRadiusDegrees * Math.cos(angle);
    const newLng = lng + offsetRadiusDegrees * Math.sin(angle);

    return { lat: newLat, lng: newLng };
  };

  const offersFromSearch = computed<ExtendedAlgoliaOffer[]>(() => {
    if (isLoading.value) {
      return [];
    }

    return offers.value;
  });

  const getCoordinateKey = (geoloc: GoogleMapLatLngLiteral) => `${geoloc.lat},${geoloc.lng}`;

  const antiCollisionAddressesIndex = computed<Map<string, Map<string, number>>>(() => offersFromSearch.value.reduce((addresses, offer) => {
    const key = getCoordinateKey(offer._geoloc);
    const addressId = offer.address.id;

    if (!addresses.has(key)) {
      addresses.set(key, new Map());
    }

    const addressIndex = addresses.get(key)!;

    if (!addressIndex.has(addressId)) {
      addressIndex.set(addressId, addressIndex.size);
    }

    return addresses;
  }, new Map<string, Map<string, number>>()));

  const getMarkerPositionAvoidingCollisions = (offer: ExtendedAlgoliaOffer): GoogleMapLatLngLiteral => {
    const key = getCoordinateKey(offer._geoloc);
    const addressIndex = antiCollisionAddressesIndex.value.get(key) as Map<string, number>;
    const total = addressIndex.size;
    const index = addressIndex.get(offer.address.id) ?? -1;

    const hasCollision = total > 1 && index !== -1;

    if (!hasCollision) {
      return {
        lat: offer._geoloc.lat,
        lng: offer._geoloc.lng,
      };
    }

    return getOffsetLatLng({
      lat: offer._geoloc.lat,
      lng: offer._geoloc.lng,
      index,
      total,
    });
  };

  const markers = computed<SearchMapMarker[]>(() => offersFromSearch.value.map((offer) => {
    const { lat, lng } = getMarkerPositionAvoidingCollisions(offer);

    return {
      id: offer.hash_id,
      title: currencyNoDecimal(getTotalMonthlyPrice(offer)),
      lat,
      lng,
      type: MapMarkerType.PRIMARY,
      groupedMarkerTitles: (offer.grouped_offers ?? []).map((groupedOffer) => currencyNoDecimal(getTotalMonthlyPrice(groupedOffer))),
    };
  }));

  const primaryMarkers = computed<SearchMapMarker[]>(() => markers.value.slice(0, perPage.value));

  const secondaryMarkers = computed<SearchMapMarker[]>(
    () => markers.value.slice(perPage.value).map((marker) => ({
      ...marker,
      type: MapMarkerType.SECONDARY,
    })),
  );

  const clickedMarkers = ref<string[]>([]);
  const selectedOfferHashId = ref<string | null>(null);

  const closeOfferPanel = (): void => {
    selectedOfferHashId.value = null;
  };

  const isMarkerClicked = (markerId: string): boolean => clickedMarkers.value.some((clickedMarkerId) => clickedMarkerId === markerId);

  const addMarkerToClicked = (markerId: string): void => {
    if (isMarkerClicked(markerId)) {
      return;
    }

    clickedMarkers.value.push(markerId);
  };

  const updateCustomMarkerAnchorPoint = (markerId: string): void => {
    const mapCenter = map.value?.getCenter();
    if (!mapCenter) {
      customMarkerAnchorPoint.value = 'BOTTOM_RIGHT';
      return;
    }

    const marker = markers.value.find((item) => item.id === markerId);
    if (!marker) {
      customMarkerAnchorPoint.value = 'BOTTOM_RIGHT';
      return;
    }

    const isTop = marker.lat >= mapCenter.lat();
    const isRight = marker.lng >= mapCenter.lng();

    if (isTop && isRight) {
      customMarkerAnchorPoint.value = 'BOTTOM_LEFT';
      return;
    }

    if (isTop && !isRight) {
      customMarkerAnchorPoint.value = 'BOTTOM_RIGHT';
      return;
    }

    if (!isTop && isRight) {
      customMarkerAnchorPoint.value = 'TOP_LEFT';
      return;
    }

    customMarkerAnchorPoint.value = 'TOP_RIGHT';
  };

  const triggerOfferPanel = (markerId: string): void => {
    if (selectedOfferHashId.value === markerId) {
      selectedOfferHashId.value = null;
    } else {
      selectedOfferHashId.value = markerId;
      updateCustomMarkerAnchorPoint(markerId);
    }

    addMarkerToClicked(markerId);
  };

  const initMap = (newMap: GoogleMap): void => {
    initGoogleMap(newMap);
    fitMapToBounds(getFranceBounds());

    newMap.addListener('click', closeOfferPanel);
  };

  const geolocate = (): void => {
    if (!navigator?.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      const coords: GoogleMapLatLngLiteral = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      userLocation.value = coords;

      if (!map.value) {
        center.value = coords;
        return;
      }

      map.value.setCenter(coords);
      map.value.setZoom(14);
    });
  };

  return {
    center,
    customMarkerAnchorPoint,
    primaryMarkers,
    secondaryMarkers,
    userLocation,
    selectedOfferHashId,
    zoomIn,
    zoomOut,
    initMap,
    geolocate,
    addMarkerToClicked,
    isMarkerClicked,
    triggerOfferPanel,
    closeOfferPanel,
  };
});
